import { appendBrandIds } from '@/lib/brand-filter-params';
import { ApiError } from '@/lib/pos-providers-client';
import type { PaymentPreference } from '@/types/brand';
import { SELL_CHANNELS, type SellChannel } from '@/types/sell-channel';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002';

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
}

async function api<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body } = options;
  const url = `${API_BASE_URL}${endpoint}`;

  const fetchOptions: RequestInit = {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  };

  if (body && method !== 'GET') {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(url, fetchOptions);

  if (response.status === 401 || response.status === 403) {
    window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { status: response.status } }));
    throw new Error(`Unauthorized (${response.status})`);
  }

  if (!response.ok) {
    // Structured, so a form can map a validation `code` (C11) to an inline error instead of
    // printing the raw body. The agent's exception filter writes the code as `error_code`; a body
    // forwarded whole from elsewhere may carry it as `code`, so both are read.
    let message = `API error (${response.status})`;
    let code: string | undefined;
    let details: Record<string, unknown> | undefined;
    try {
      const body = await response.json();
      const bodyMessage = Array.isArray(body?.message) ? body.message.join('; ') : body?.message;
      if (typeof bodyMessage === 'string' && bodyMessage) message = bodyMessage;
      const bodyCode = body?.error_code ?? body?.code;
      code = typeof bodyCode === 'string' ? bodyCode : undefined;
      // The filter forwards a refusal's extra numbers (the phone cap's `current`/`limit`/`attempted`)
      // under `details` only (C11 rev 2.1).
      if (body?.details && typeof body.details === 'object') details = body.details;
    } catch {
      // Not JSON — keep the status-only message.
    }
    throw new ApiError(message, { code, status: response.status, details });
  }

  return response.json();
}

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

/**
 * `DELIVERY_FEE` takes money off the delivery fee and nothing else: its `value` is the percentage
 * off the fee, 1..100 (100 = envío gratis).
 */
export type DiscountType = 'CART_PERCENT' | 'CART_FIXED' | 'ITEM_DISCOUNT' | 'ITEM_QUANTITY_TIERS' | 'DELIVERY_FEE';
/** `UNSUPPORTED` is POS-only: the POS is running the discount on a condition Menteo cannot honour. */
export type DiscountStatus = 'ACTIVE' | 'DISABLED' | 'UNSUPPORTED';

/** Who authored the discount — and therefore whether the merchant may edit it here. */
export type DiscountSource = 'MERCHANT' | 'POS';

/**
 * Which of the POS's own two lists a synced row came from (§C1). Only POS rows carry it: Rhino
 * models "descuento" (automatic) and "cupón" (coded) as separate objects, and the hub needs to know
 * which one it is looking at without re-deriving it from `code`.
 */
export type DiscountPosChannel = 'DESCUENTO' | 'CUPON';

/**
 * Why a merchant row is switched off by something other than the merchant (§C1).
 *
 * `POS_OWNS_DISCOUNTS` is set by the sync on a coupon-syncing POS brand: the POS decides discounts
 * there, so our rows are parked rather than deleted — re-enabling them is a matter of the POS, not
 * of a click here.
 */
export type DiscountDisabledReason = 'POS_OWNS_DISCOUNTS';

/** Empty/null on a coupon means both fulfilment types qualify. */
export type DiscountDeliveryType = 'delivery' | 'pickup';

export type DiscountDayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export interface DiscountTimeRange {
  /** 'HH:mm', 24h. */
  open: string;
  close: string;
}

/**
 * Weekly recurrence windows — the "sólo lunes, martes y miércoles" half of a discount's validity,
 * with `startsAt`/`endsAt` bounding the season it repeats inside ("válido todo el año").
 *
 * Same per-day shape as the backend's `OperatingHours`, with ONE rule of its own: a discount window
 * MAY cross midnight (`{ open: '22:00', close: '02:00' }` is Tuesday night into Wednesday morning).
 * Store hours and catalog timetables still may not — do not copy this leniency into those editors.
 * A whole day is `{ open: '00:00', close: '00:00' }`: a `00:00` CLOSE means end of day.
 */
export type DiscountScheduleHours = Partial<Record<DiscountDayOfWeek, DiscountTimeRange[]>>;

/**
 * A menu section a discount is aimed at: the id that matches, plus the name as it read when the
 * merchant picked it.
 *
 * The name travels with the id on purpose — it is what every surface (this UI, the agent's prompt,
 * the storefront, an order's frozen snapshot) prints without having to re-read the catalog. A brand
 * with several branches legitimately holds SEVERAL refs for one section the merchant thinks of as
 * one, because categories are per-menu rows; the pickers here group them back by name.
 */
export interface DiscountCategoryRef {
  id: string;
  name?: string | null;
}

/**
 * A set of menu entries named by item, by section, or by both — how a discount says "these dishes".
 *
 * Empty (neither list) means EVERYTHING, the same convention `locationIds` and `allowedChannels`
 * follow. A category is a live reference: adding a dish to the section later puts it in the deal, so
 * nothing is expanded into item ids when the promotion is saved.
 */
export interface DiscountItemSelector {
  catalogItemIds?: string[] | null;
  categories?: DiscountCategoryRef[] | null;
}

export interface DiscountRewardItem {
  /** The named dish. Exactly one of this and `category` is set. */
  catalogItemId?: string | null;
  /**
   * A whole section instead of a dish — "un postre gratis" rather than "un flan gratis".
   *
   * A category reward is never added to the cart on the customer's behalf (nobody but them chooses
   * which dessert); it discounts a qualifying line they already have, dearest first.
   */
  category?: DiscountCategoryRef | null;
  /** Units the discount reaches. A cart holding more keeps the excess at full price. */
  quantity: number;
  /** 100 = free. */
  discountPercent: number;
}

/**
 * How a quantity ladder turns units bought into units discounted. Picked once per promotion — the
 * two are different deals, not two ways of writing the same one.
 *
 * With a 2→10%, 3→20% ladder and 5 units in the cart: `THRESHOLD` gives 20% on all five,
 * `BUNDLE` gives 20% on three plus 10% on two, and at 4 units `BUNDLE` leaves the 4th at full price.
 */
export type QuantityTierMode = 'THRESHOLD' | 'BUNDLE';

/** One rung: buy this many of the promotion's items, get this percentage off each of them. */
export interface DiscountQuantityTier {
  /** At least 2 — a 1-unit rung would be an unconditional item discount. */
  quantity: number;
  /** Percentage off each covered unit's full price, modifiers included. */
  discountPercent: number;
}

/**
 * A "2x" promotion. Units are POOLED across `catalogItemIds` — two different pizzas reach the 2-unit
 * rung together — and in `BUNDLE` mode the dearest units are packed first.
 *
 * Automatic promotions only: the API refuses a ladder on a coded coupon, because the deal is earned
 * by what is in the cart rather than by typing anything.
 */
export interface DiscountQuantityTiers {
  mode: QuantityTierMode;
  /** Named dishes in the pool. May be empty when `categories` names it instead. */
  catalogItemIds?: string[] | null;
  /** Whole sections in the pool — "llevá 3 cervezas" without listing every beer. */
  categories?: DiscountCategoryRef[] | null;
  tiers: DiscountQuantityTier[];
}

/**
 * How the storefront advertises a discount — a display preference, never a rule about what applies.
 *
 * `AUTO`: an automatic promotion is shown where its shape calls for (a banner for a cart-wide deal, a
 * badge on the dishes for an item-scoped one); a coded coupon is NOT shown — codes are handed out, not
 * published. `FEATURED`: a banner regardless of shape, and for a coupon this PUBLISHES THE CODE on the
 * storefront. `HIDDEN`: never shown; it still applies to every qualifying cart.
 */
export type DiscountStorefrontDisplay = 'AUTO' | 'FEATURED' | 'HIDDEN';

export const DISCOUNT_STOREFRONT_DISPLAYS: readonly DiscountStorefrontDisplay[] = ['AUTO', 'FEATURED', 'HIDDEN'];

export interface Discount {
  id: string;
  brandId: string;
  /** Set by aggregate-aware responses so a row can be labeled without a second lookup. */
  brandName?: string;
  /**
   * Merchant-authored, or pulled from the brand's POS. A `POS` row is written only by the menu sync
   * and is READ-ONLY here: an edit would be reverted by the next sync.
   */
  source: DiscountSource;
  /** The POS's own id for the discount (Rhino's `descuento_id`); null on merchant-authored rows. */
  posExternalId?: string | null;
  /**
   * POS rows only: the synced menu prices are already net of this discount, so nothing applies it to
   * a cart — it exists so the order push can name it and stop the POS re-pricing at gross.
   */
  reflectedInPrices?: boolean;
  /**
   * Null marks an *automatic promotion*: a codeless discount the agent attaches on its own whenever
   * the conditions below hold. Everything else about the entity is identical, which is why the three
   * flavours share this type — the Discounts hub splits them client-side on `code` and `source`.
   */
  code: string | null;
  /** Display name. Required for codeless promotions (there is no code to call them by). */
  name?: string | null;
  description?: string | null;
  type: DiscountType;
  value: number;
  rewardItems?: DiscountRewardItem[] | null;
  /** The ladder of an `ITEM_QUANTITY_TIERS` promotion; null for every other type. */
  quantityTiers?: DiscountQuantityTiers | null;
  /**
   * The part of the menu a `CART_PERCENT` / `CART_FIXED` discount is computed on; null = the whole
   * cart. This is what "20% en Pizzas" is. Always null on the item-scoped types, which aim
   * themselves through `rewardItems` / `quantityTiers` — the API refuses a scope on those.
   */
  itemScope?: DiscountItemSelector | null;
  /** Checked only when the customer confirms, never when they type the code. */
  minimumOrderAmount?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  excludePromoItems: boolean;
  maxUsesPerCustomer?: number | null;
  maxUsesTotal?: number | null;
  status: DiscountStatus;
  /** Null or empty = any payment method qualifies. */
  paymentMethods?: PaymentPreference[] | null;
  /** Null or empty = both delivery and pickup qualify. */
  deliveryTypes?: DiscountDeliveryType[] | null;
  /**
   * The branches the discount runs at, as location ids; null/empty = all of them.
   *
   * Editable on merchant promotions. On a POS row it is READ-ONLY and reports what the POS itself is
   * running: Rhino's `descuento` list is per sucursal, so a descuento switched off at one branch comes
   * back scoped to the branches that still serve it.
   */
  locationIds?: string[] | null;
  /**
   * The sell channels the coupon may be used on; null or empty = every one of them.
   *
   * Coded coupons only: the API refuses a non-empty list on an automatic promotion, because nothing
   * on the automatic path judges the channel — the deal is earned by the cart, not by typing a code.
   * A code typed on a channel outside the list is refused with `COUPON_CHANNEL_NOT_ALLOWED`.
   */
  allowedChannels?: SellChannel[] | null;
  /** Null = no weekly restriction. Evaluated live on every recompute, in `scheduleTimezone`. */
  scheduleHours?: DiscountScheduleHours | null;
  /** IANA zone the weekly windows are read in. Backend defaults to 'America/Montevideo'. */
  scheduleTimezone?: string | null;
  /** Storefront display preference. Absent on an older API = `AUTO`. */
  storefrontDisplay?: DiscountStorefrontDisplay;
  /**
   * Whether this discount may ride along with another one on the same cart.
   *
   * Merchant rows: the merchant's own choice, default off. POS rows: read-only, it is what the POS
   * itself says (Rhino's `no_acumulable === 0`), so the hub reports it rather than offering it.
   */
  stackable: boolean;
  /**
   * A whole-cart `CART_PERCENT` / `CART_FIXED` that also discounts the delivery fee: a percentage
   * takes the same percentage off the fee, a fixed amount spills what the items could not absorb.
   * False on every other shape (a `DELIVERY_FEE` is fee-only by type and never carries it). Absent
   * on an older API = false.
   */
  appliesToDeliveryFee?: boolean;
  /**
   * How many phones the discount is limited to (C1); 0 = everyone. The list itself is never on this
   * object — it is paged through `listEligiblePhones`. Absent on an older API = 0.
   */
  eligiblePhoneCount?: number;
  /** POS rows only — which of the POS's lists this came from. Null/absent on merchant rows. */
  posChannel?: DiscountPosChannel | null;
  /** Set by the sync, never by the merchant. Null/absent when the merchant switched the row off. */
  disabledReason?: DiscountDisabledReason | null;
  /**
   * The POS's own redemption count (Rhino's `amount_uses`), display-only and labelled as the POS's.
   * Null when the POS did not say. NEVER mixed with our `DiscountUsageStats.redemptions`.
   */
  posUsageCount?: number | null;
  isSandbox: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DiscountUsageStats {
  redemptions: number;
  discountGiven: number;
  /** Total value of the orders that used the coupon, POST-discount — what was actually charged. */
  revenue: number;
  /**
   * Distinct customers who redeemed. Read against `redemptions` this separates a campaign that
   * reached many people from one a handful of regulars used repeatedly.
   */
  uniqueCustomers: number;
  averageOrderValue: number;
  averageDiscount: number;
  /** Null until the promotion has converted at least once. */
  firstRedeemedAt: string | null;
  lastRedeemedAt: string | null;
}

/** One row of the coupon's order list. The order detail dialog supplies everything beyond this. */
export interface DiscountRedemptionOrder {
  orderId: string;
  redeemedAt: string;
  /** What this order got off — the frozen per-redemption amount, not the coupon's current config. */
  discountAmount: number;
  orderTotal: number;
  /**
   * The order's status. Left as a plain string rather than importing `OrderStatus`: orders-client
   * already imports the coupon types for its `appliedDiscount` snapshot, and importing back would put
   * a cycle between the two clients for a value this module only ever passes through.
   */
  orderStatus: string;
  currency: string;
  deliveryType?: 'delivery' | 'pickup' | null;
  customerId: string;
  customerName?: string | null;
  customerPhone?: string | null;
}

export interface DiscountInput {
  /** Explicit `null` creates an automatic promotion; omit it on update (the kind is immutable). */
  code?: string | null;
  name?: string | null;
  description?: string | null;
  type?: DiscountType;
  value?: number;
  rewardItems?: DiscountRewardItem[] | null;
  quantityTiers?: DiscountQuantityTiers | null;
  /** Cart-wide types only; `null` or an empty selector means the whole cart. */
  itemScope?: DiscountItemSelector | null;
  minimumOrderAmount?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  excludePromoItems?: boolean;
  maxUsesPerCustomer?: number | null;
  maxUsesTotal?: number | null;
  status?: DiscountStatus;
  paymentMethods?: PaymentPreference[] | null;
  deliveryTypes?: DiscountDeliveryType[] | null;
  /** Branch scope; `null` or `[]` means every branch. Rejected by the API on a POS-managed row. */
  locationIds?: string[] | null;
  /**
   * Channel scope; `null` or `[]` means every channel. Coupons only — send nothing at all for an
   * automatic promotion, a non-empty list there is a 400.
   */
  allowedChannels?: SellChannel[] | null;
  scheduleHours?: DiscountScheduleHours | null;
  scheduleTimezone?: string | null;
  storefrontDisplay?: DiscountStorefrontDisplay;
  /** Merchant rows only; the API refuses a write on a POS row. Absent = false. */
  stackable?: boolean;
  /**
   * Whole-cart `CART_PERCENT` / `CART_FIXED` only (empty `itemScope`); never sent as true on a
   * `DELIVERY_FEE`, where it is implicit. Anything else is a 400 `DELIVERY_FEE_SCOPE_INVALID`.
   */
  appliesToDeliveryFee?: boolean;
  /**
   * Create only (C2): the initial phone list, canonicalized server-side, at most
   * {@link ELIGIBLE_PHONES_LIMIT}. Absent/empty = everyone. Any invalid number refuses the whole
   * create (422 `ELIGIBLE_PHONE_INVALID`, see {@link EligibilityError.rejected}). Edited afterwards
   * through `updateEligiblePhones`, never by a PATCH.
   */
  eligiblePhones?: string[];
}

/** Most phones one discount may be limited to (D1). The API enforces it; the UI only shows the count. */
export const ELIGIBLE_PHONES_LIMIT = 500;

/** A phone the API refused for a list, echoed back verbatim with the reason (C2). */
export interface RejectedEligiblePhone {
  phone: string;
  reason: 'INVALID' | 'INSTAGRAM_ID';
}

/** A discount with a phone list reaches only those customers (D5). */
export function isPhoneRestricted(discount: Pick<Discount, 'eligiblePhoneCount'>): boolean {
  return (discount.eligiblePhoneCount ?? 0) > 0;
}

/** The C11 codes a phone-list refusal carries; the editor renders each inline. */
export const ELIGIBILITY_ERROR_CODES = [
  'ELIGIBLE_PHONES_LIMIT',
  'ELIGIBLE_PHONE_INVALID',
  'RESTRICTED_CANNOT_BE_FEATURED',
  'RESTRICTED_POS_ROW',
] as const;

export type EligibilityErrorCode = (typeof ELIGIBILITY_ERROR_CODES)[number];

export interface EligibilityError {
  code: EligibilityErrorCode;
  /** `ELIGIBLE_PHONES_LIMIT` only, from the body's `details` (C11 rev 2.1). Never parsed from the message. */
  current?: number;
  limit?: number;
  attempted?: number;
  /** `ELIGIBLE_PHONE_INVALID` only (422), from `details.rejected`: every number that refused the write. */
  rejected?: RejectedEligiblePhone[];
}

/** The phone-list refusal a failed call carries, or null. An allowlist, like `deliveryFeeErrorCode`. */
export function eligibilityError(err: unknown): EligibilityError | null {
  if (!(err instanceof ApiError)) return null;
  if (!ELIGIBILITY_ERROR_CODES.includes(err.code as EligibilityErrorCode)) return null;
  const number = (key: string) => (typeof err.details?.[key] === 'number' ? (err.details[key] as number) : undefined);
  return {
    code: err.code as EligibilityErrorCode,
    current: number('current'),
    limit: number('limit'),
    attempted: number('attempted'),
    rejected: Array.isArray(err.details?.rejected) ? (err.details.rejected as RejectedEligiblePhone[]) : undefined,
  };
}

/** Status pills. Both modes explicitly, per the theming rules — these carry meaning. */
export const DISCOUNT_STATUS_COLORS: Record<DiscountStatus, string> = {
  UNSUPPORTED: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  ACTIVE: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300',
  DISABLED: 'bg-muted text-muted-foreground',
};

export const DISCOUNT_TYPE_COLORS: Record<DiscountType, string> = {
  CART_PERCENT: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
  CART_FIXED: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
  ITEM_DISCOUNT: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300',
  ITEM_QUANTITY_TIERS: 'bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-300',
  DELIVERY_FEE: 'bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-300',
};

/** The types the "También descuenta el envío" flag is legal on — and then only with no item scope. */
export function acceptsDeliveryFeeFlag(type: DiscountType): boolean {
  return type === 'CART_PERCENT' || type === 'CART_FIXED';
}

/**
 * What a discount does to the delivery fee, or null when it leaves the fee alone — the one reading
 * every badge, list row and detail panel renders its fee copy from.
 *
 * `free` is a `DELIVERY_FEE` at 100% ("Envío gratis"), `percent` one below it ("X% en el envío"),
 * and `included` a whole-cart discount carrying the flag ("Incluye envío").
 */
export type DeliveryFeeDeal = { kind: 'free' } | { kind: 'percent'; percent: number } | { kind: 'included' };

export function deliveryFeeDeal(
  discount: Pick<Discount, 'type' | 'value' | 'appliesToDeliveryFee'>,
): DeliveryFeeDeal | null {
  if (discount.type === 'DELIVERY_FEE') {
    return discount.value >= 100 ? { kind: 'free' } : { kind: 'percent', percent: discount.value };
  }
  return discount.appliesToDeliveryFee === true ? { kind: 'included' } : null;
}

/** The C11 validation codes a discount form turns into an inline error rather than a raw message. */
export const DELIVERY_FEE_ERROR_CODES = ['DELIVERY_FEE_SCOPE_INVALID', 'DELIVERY_FEE_DISCOUNT_UNSUPPORTED'] as const;

export type DeliveryFeeErrorCode = (typeof DELIVERY_FEE_ERROR_CODES)[number];

/**
 * The delivery-fee validation code a failed save carries, or null. An allowlist rather than a
 * truthiness check: the API codes EVERY error (see `ApiError`), so a looser test would catch a 404.
 */
export function deliveryFeeErrorCode(err: unknown): DeliveryFeeErrorCode | null {
  if (!(err instanceof ApiError)) return null;
  return DELIVERY_FEE_ERROR_CODES.includes(err.code as DeliveryFeeErrorCode)
    ? (err.code as DeliveryFeeErrorCode)
    : null;
}

/**
 * The menu sections a selector covers, de-duplicated and in the order they were picked.
 *
 * De-duplication is the point: a two-branch brand's "Pizzas" is two category rows, and a row reading
 * "Pizzas, Pizzas" looks like a bug. Refs with no stored name are skipped rather than printed as raw
 * ids — an id nobody can read is worse than a shorter list.
 */
export function selectorCategoryNames(selector?: DiscountItemSelector | null): string[] {
  const names = (selector?.categories ?? [])
    .map((category) => category.name?.trim())
    .filter((name): name is string => Boolean(name));
  return [...new Set(names)];
}

/**
 * What part of the menu a cart-wide discount is limited to — "Pizzas", "Pizzas, Postres" — or null
 * when it covers the whole cart.
 *
 * Only the section NAMES are rendered. A scope that also names loose items says so with a count
 * rather than a list of ids: the ids mean nothing to a merchant reading a table row, and resolving
 * them would cost a catalog read on a list that renders dozens of rows.
 */
export function describeItemScope(
  selector: DiscountItemSelector | null | undefined,
  labelExtraItems: (count: number) => string,
): string | null {
  const names = selectorCategoryNames(selector);
  const looseItems = selector?.catalogItemIds?.length ?? 0;
  if (!names.length && !looseItems) return null;
  const parts = [...names];
  if (looseItems) parts.push(labelExtraItems(looseItems));
  return parts.join(', ');
}

/**
 * The ladder as one short phrase — "2+ → 10%, 3+ → 20%" or "2 → 10%, 3 → 20%".
 *
 * The `+` is the whole difference between the modes, and it is the part a merchant checks when they
 * reopen a promotion they configured last month.
 */
export function describeQuantityTiers(tiers: DiscountQuantityTiers): string {
  const suffix = tiers.mode === 'THRESHOLD' ? '+' : '';
  return [...tiers.tiers]
    .sort((a, b) => a.quantity - b.quantity)
    .map((tier) => `${tier.quantity}${suffix} → ${tier.discountPercent}%`)
    .join(', ');
}

/**
 * A coupon can read as ACTIVE and still not work: its window may not have opened yet, may have
 * closed, or its global cap may be spent. The list has to say which, or a merchant sees "activo"
 * next to a code customers are being told is invalid.
 */
export type DiscountEffectiveState = 'active' | 'disabled' | 'scheduled' | 'expired' | 'exhausted' | 'unsupported';

export function discountEffectiveState(
  coupon: Discount,
  stats?: DiscountUsageStats,
  now = new Date(),
): DiscountEffectiveState {
  // First: the POS is running it on a condition we cannot reproduce, so no window and no toggle of
  // ours changes anything about it. Anything below would read as a state the merchant could act on.
  if (coupon.status === 'UNSUPPORTED') return 'unsupported';
  if (coupon.status === 'DISABLED') return 'disabled';
  if (coupon.startsAt && now < new Date(coupon.startsAt)) return 'scheduled';
  if (coupon.endsAt && now > new Date(coupon.endsAt)) return 'expired';
  if (coupon.maxUsesTotal != null && stats && stats.redemptions >= coupon.maxUsesTotal) return 'exhausted';
  return 'active';
}

export const DISCOUNT_STATE_COLORS: Record<DiscountEffectiveState, string> = {
  active: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300',
  disabled: 'bg-muted text-muted-foreground',
  scheduled: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
  expired: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300',
  exhausted: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
  unsupported: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
};

/**
 * The four buckets the Cupones and Descuentos lists are filtered by (D7).
 *
 * Client-side filters over one list, not four queries — and derived from `discountEffectiveState`
 * so "vencido" cannot come to mean one thing in a pill and another in a row's state chip.
 */
export type DiscountStatusFilter = 'current' | 'expired' | 'disabled' | 'unsupported';

/** Pill order, left to right. `current` is the default selection on both tabs. */
export const DISCOUNT_STATUS_FILTERS: readonly DiscountStatusFilter[] = [
  'current',
  'expired',
  'disabled',
  'unsupported',
];

/**
 * Which pill a row sits under.
 *
 * Takes no stats on purpose: a row that has spent its cap is still *vigente* as far as the merchant
 * is concerned (it is on, and raising the cap brings it straight back), and so is one whose window
 * has not opened yet. Only a closed window moves a row to Vencidos, and `DISABLED` wins over it —
 * a switched-off row belongs under Deshabilitados whatever its dates say.
 */
export function discountStatusFilter(discount: Discount, now = new Date()): DiscountStatusFilter {
  const state = discountEffectiveState(discount, undefined, now);
  if (state === 'unsupported') return 'unsupported';
  if (state === 'disabled') return 'disabled';
  if (state === 'expired') return 'expired';
  return 'current';
}

/**
 * POS integrations that CARRY our discounts: we author the deal, they accept the priced order.
 *
 * Lowercase because that is what `PosProviderType` and the provider rows actually hold — §C8 writes
 * the types in caps, but a capitalised entry here would never match a real provider.
 */
const DISCOUNT_CARRYING_POS_TYPES: readonly string[] = ['ambit', 'shopify'];

/**
 * POS integrations that run their OWN discounts and sync them down to us (Rhino).
 *
 * Their brands still get the discounts surface — that is the only place the merchant can see what
 * the POS is running — but nothing here may author a discount for them (D9).
 */
const COUPON_SYNCING_POS_TYPES: readonly string[] = ['rhino'];

/**
 * Whether the discounts surface shows at all, given the brand's *enabled* POS provider types (§C8).
 *
 * An empty list means the brand has no POS, which also qualifies — there is no external system
 * whose pricing the discount would have to survive.
 */
export function couponsSurfaceEnabled(enabledPosTypes: string[]): boolean {
  return enabledPosTypes.every(
    (type) => DISCOUNT_CARRYING_POS_TYPES.includes(type) || COUPON_SYNCING_POS_TYPES.includes(type),
  );
}

/**
 * Whether the merchant may AUTHOR discounts here (§C8) — the narrower half of the gate.
 *
 * False on a POS that owns its own discounts: the create buttons come off and every row is
 * read-only, while `couponsSurfaceEnabled` keeps the tabs themselves visible.
 */
export function canAuthorDiscounts(enabledPosTypes: string[]): boolean {
  return enabledPosTypes.every((type) => DISCOUNT_CARRYING_POS_TYPES.includes(type));
}

/**
 * Automatic promotions and coded coupons live in the same list; this is the only thing that tells
 * them apart. Use it rather than testing `code` inline so the convention stays in one place.
 */
export function isAutomaticPromotion(discount: Discount): boolean {
  return discount.code == null;
}

/**
 * The branches a discount runs at, named — or null when it runs at all of them.
 *
 * Names rather than ids, because a merchant reading "Solo: The Bar" learns something and a merchant
 * reading a uuid does not. An id with no matching branch is dropped: the branch was deleted after the
 * discount was scoped, and the next POS sync narrows the row anyway.
 */
export function discountBranchNames(
  discount: Pick<Discount, 'locationIds'>,
  locations: Array<{ id: string; name: string }>,
): string[] | null {
  if (!discount.locationIds?.length) return null;
  const namesById = new Map(locations.map((location) => [location.id, location.name]));
  return discount.locationIds.map((id) => namesById.get(id)).filter((name): name is string => !!name);
}

/**
 * The channels a discount is LIMITED to, or null when it runs on all of them.
 *
 * A list holding every channel is not a restriction, and neither is an empty one — both are how the
 * backend spells "anywhere" — so the list and the detail panel only show the indicator for a genuine
 * subset. Values this client does not know are kept: a channel added on the backend still narrows
 * the coupon, and dropping it would make a restricted coupon read as unrestricted.
 */
export function restrictedChannels(discount: Pick<Discount, 'allowedChannels'>): SellChannel[] | null {
  const channels = discount.allowedChannels;
  if (!channels?.length) return null;
  return SELL_CHANNELS.every((channel) => channels.includes(channel)) ? null : channels;
}

/** A discount the brand's POS authored: shown, never edited here, and kept in step by the menu sync. */
export function isPosDiscount(discount: Discount): boolean {
  return discount.source === 'POS';
}

/** A merchant-authored automatic promotion — codeless AND ours to edit. */
export function isMerchantPromotion(discount: Discount): boolean {
  return discount.code == null && discount.source !== 'POS';
}

/**
 * The discounts on a cart or an order, as a list — the ONE place that still knows the singular
 * `appliedDiscount` ever existed.
 *
 * A cart or order read during the deploy window answers the pre-multi-discount shape, and every
 * surface that renders a discount line has to tolerate it. Normalising here (rather than in each
 * of them) is what keeps the old field from growing a second life: nothing else reads it.
 */
export function appliedDiscountList<T>(source: { appliedDiscounts?: T[] | null; appliedDiscount?: T | null }): T[] {
  return source.appliedDiscounts ?? (source.appliedDiscount ? [source.appliedDiscount] : []);
}

export const discountsClient = {
  async list(params?: { brandIds?: string[] }): Promise<ApiResponse<Discount[]>> {
    const searchParams = new URLSearchParams();
    appendBrandIds(searchParams, params?.brandIds);
    const query = searchParams.toString();
    return api<ApiResponse<Discount[]>>(`/discounts${query ? `?${query}` : ''}`);
  },

  async getById(discountId: string): Promise<ApiResponse<{ coupon: Discount; stats: DiscountUsageStats }>> {
    return api<ApiResponse<{ coupon: Discount; stats: DiscountUsageStats }>>(`/discounts/${discountId}`);
  },

  /** The coupon's conversions. Separate from `getById` so the list page's per-row stats stay cheap. */
  async listOrders(discountId: string, limit = 100): Promise<ApiResponse<DiscountRedemptionOrder[]>> {
    return api<ApiResponse<DiscountRedemptionOrder[]>>(`/discounts/${discountId}/orders?limit=${limit}`);
  },

  /** `brandId` is required for aggregate sessions — a coupon belongs to exactly one brand's menu. */
  async create(input: DiscountInput, options?: { brandId?: string }): Promise<ApiResponse<Discount>> {
    return api<ApiResponse<Discount>>('/discounts', {
      method: 'POST',
      body: options?.brandId ? { ...input, brandId: options.brandId } : input,
    });
  },

  /** One page of the phone list (canonical digits) — the only read that returns phones. */
  async listEligiblePhones(
    discountId: string,
    offset = 0,
    limit = 100,
  ): Promise<ApiResponse<{ phones: string[]; total: number }>> {
    return api<ApiResponse<{ phones: string[]; total: number }>>(
      `/discounts/${discountId}/eligible-phones?offset=${offset}&limit=${limit}`,
    );
  },

  /** Removals apply before additions, in one transaction; the cap is judged on the final count. */
  async updateEligiblePhones(
    discountId: string,
    changes: { add?: string[]; remove?: string[] },
  ): Promise<ApiResponse<{ eligiblePhoneCount: number }>> {
    return api<ApiResponse<{ eligiblePhoneCount: number }>>(`/discounts/${discountId}/eligible-phones`, {
      method: 'POST',
      body: changes,
    });
  },

  /** A POST body on purpose: a phone must never land in a URL or an access log. */
  async checkEligibility(
    discountId: string,
    phone: string,
  ): Promise<ApiResponse<{ restricted: boolean; eligible: boolean }>> {
    return api<ApiResponse<{ restricted: boolean; eligible: boolean }>>(`/discounts/${discountId}/eligibility`, {
      method: 'POST',
      body: { phone },
    });
  },

  async update(discountId: string, input: DiscountInput): Promise<ApiResponse<Discount>> {
    return api<ApiResponse<Discount>>(`/discounts/${discountId}`, { method: 'PATCH', body: input });
  },

  /** Kill a promotion early. Never deletes: confirmed orders must stay explicable. */
  async disable(discountId: string): Promise<ApiResponse<Discount>> {
    return api<ApiResponse<Discount>>(`/discounts/${discountId}`, { method: 'PATCH', body: { status: 'DISABLED' } });
  },

  async enable(discountId: string): Promise<ApiResponse<Discount>> {
    return api<ApiResponse<Discount>>(`/discounts/${discountId}`, { method: 'PATCH', body: { status: 'ACTIVE' } });
  },
};
